import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchQuestion } from './api/interview';
import QuizPage from './components/QuizPage';
import CompletePage from './components/CompletePage';
import { resolveInterviewId } from './utils/interviewId';
import { useCountdown } from './hooks/useCountdown';
import './App.css';

function LoadingScreen() {
  return (
    <div className="status-screen">
      <div className="spinner" />
      <p>正在加载题目…</p>
    </div>
  );
}

function ErrorScreen({ message }) {
  return (
    <div className="status-screen error">
      <div className="status-icon">!</div>
      <p>{message}</p>
    </div>
  );
}

export default function App() {
  const [screen, setScreen] = useState('loading');
  const [question, setQuestion] = useState(null);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [examEndTime, setExamEndTime] = useState(null);

  const idRef = useRef('');
  const submittingRef = useRef(false);

  const loadQuestion = useCallback(async (id, answers = []) => {
    const data = await fetchQuestion(id, answers);
    setQuestion(data);
    setSelectedAnswers([]);
    setTotalCount(data.总共有多少题);
    setExamEndTime((prev) => prev ?? data.考试结束时间 ?? null);
    setScreen('quiz');
    return data;
  }, []);

  useEffect(() => {
    const interviewId = resolveInterviewId();
    if (!interviewId) {
      setError('缺少 id 参数，请通过正确的链接访问');
      setScreen('error');
      return;
    }

    idRef.current = interviewId;
    loadQuestion(interviewId).catch((err) => {
      setError(err.message || '加载题目失败');
      setScreen('error');
    });
  }, [loadQuestion]);

  const goToNext = useCallback(async () => {
    if (submittingRef.current || !question) return;

    const answers = selectedAnswers.length > 0 ? selectedAnswers : [];

    submittingRef.current = true;
    setSubmitting(true);

    try {
      await loadQuestion(idRef.current, answers);
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('答完') || msg.includes('已过期')) {
        setScreen('complete');
      } else {
        setError(msg || '加载下一题失败');
        setScreen('error');
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [question, loadQuestion, selectedAnswers]);

  const handleExamExpire = useCallback(() => {
    setScreen('complete');
  }, []);

  const { progress, remainingSeconds } = useCountdown(
    examEndTime,
    handleExamExpire
  );

  const handleToggleAnswer = useCallback(
    (optionId) => {
      if (submitting) return;

      setSelectedAnswers((prev) => {
        if (question?.题类型 === '多选') {
          return prev.includes(optionId)
            ? prev.filter((id) => id !== optionId)
            : [...prev, optionId];
        }
        return [optionId];
      });
    },
    [question, submitting]
  );

  if (screen === 'loading') return <LoadingScreen />;
  if (screen === 'error') return <ErrorScreen message={error} />;
  if (screen === 'complete') return <CompletePage totalCount={totalCount} />;

  return (
    <QuizPage
      question={question}
      selectedAnswers={selectedAnswers}
      onToggleAnswer={handleToggleAnswer}
      onNext={goToNext}
      submitting={submitting}
      examEndTime={examEndTime}
      progress={progress}
      remainingSeconds={remainingSeconds}
    />
  );
}
